import { Shape } from "../types";
import { ObjectTypes } from "../enums";
import { Line } from "../../models/shapes/Line";

/**
 * Shape filtering utilities to reduce code duplication
 */

/**
 * Filters shapes by type
 */
export function filterByType(shapes: Shape[], type: string): Shape[] {
    return shapes.filter(s => s.type === type);
}

/**
 * Filters shapes by multiple types
 */
export function filterByTypes(shapes: Shape[], types: string[]): Shape[] {
    return shapes.filter(s => types.includes(s.type));
}

/**
 * Filters shapes that are not hidden
 */
export function filterVisible(shapes: Shape[]): Shape[] {
    return shapes.filter(s => !s.hidden);
}

/**
 * Filters shapes that are hidden
 */
export function filterHidden(shapes: Shape[]): Shape[] {
    return shapes.filter(s => s.hidden);
}

/**
 * Filters shapes that are selected
 */
export function filterSelected(shapes: Shape[]): Shape[] {
    return shapes.filter(s => s.isSelected);
}

/**
 * Filters shapes that are not guides
 */
export function filterNonGuides(shapes: Shape[]): Shape[] {
    return shapes.filter(s => s.type !== "guide");
}

/**
 * Common shape filter combinations
 */
export function getDisplayedShapes(shapes: Shape[]): Shape[] {
    return filterNonGuides(filterVisible(shapes));
}

export function getSelectedShapes(shapes: Shape[]): Shape[] {
    return filterSelected(getDisplayedShapes(shapes));
}

export function getLinesIfSelected(shapes: Shape[]): Line[] {
    const selectedLines = filterByType(getSelectedShapes(shapes), ObjectTypes.line) as Line[];
    const displayedLines = filterByType(getDisplayedShapes(shapes), ObjectTypes.line) as Line[];
    return selectedLines.length ? selectedLines : displayedLines;
}

/**
 * Type guard functions
 */
export function isLine(shape: Shape): shape is Shape & { type: 'line' } {
    return shape.type === ObjectTypes.line;
}

export function isCircle(shape: Shape): shape is Shape & { type: 'circle' } {
    return shape.type === ObjectTypes.circle;
}

export function isRectangle(shape: Shape): shape is Shape & { type: 'rectangle' } {
    return shape.type === ObjectTypes.rectangle;
}

export function isBlock(shape: Shape): shape is Shape & { type: 'block' } {
    return shape.type === ObjectTypes.block;
}

export function isElbowBlock(shape: Shape): shape is Shape & { type: 'elbowBlock' } {
    return shape.type === ObjectTypes.elbowBlock;
}

export function isTee(shape: Shape): shape is Shape & { type: 'tee' } {
    return shape.type === ObjectTypes.tee;
}

/**
 * Checks if all shapes in array are of the same type
 */
export function checkSameType(shapes: Shape[]): boolean {
    if (shapes.length === 0) return true;
    const firstType = shapes[0].type;
    return shapes.every(s => s.type === firstType);
}

/**
 * Gets the type of the first shape in array
 */
export function getFirstType(shapes: Shape[]): string | undefined {
    return shapes[0]?.type;
}

/**
 * Type-safe property access helpers
 */
export function getShapeProperty<T>(shape: Shape, property: string): T | undefined {
    return (shape as any)[property];
}

export function hasShapeProperty(shape: Shape, property: string): boolean {
    return property in shape;
}

/**
 * Safe property access for common shape properties
 */
export function getShapeStart(shape: Shape): any {
    return (shape as any).start;
}

export function getShapeEnd(shape: Shape): any {
    return (shape as any).end;
}

export function getShapeCenter(shape: Shape): any {
    return (shape as any).center;
}

export function getShapeRadius(shape: Shape): any {
    return (shape as any).radius;
}

export function getShapeNet(shape: Shape): any {
    return (shape as any).net;
}

export function getShapeRouteId(shape: Shape): any {
    return (shape as any).routeId;
}
